/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { classes } from '@adobe-commerce/elsie/lib';
import { HTMLAttributes } from 'preact/compat';
import '@adobe-commerce/elsie/components/TextArea/TextArea.css';
import { useRef, useEffect, useId } from 'preact/hooks';

export interface TextAreaProps extends HTMLAttributes<HTMLTextAreaElement> {
  id?: string;
  name?: string;
  disabled?: boolean;
  errorMessage?: string;
}

export const TextArea: FunctionComponent<TextAreaProps> = ({
  disabled,
  name = '',
  errorMessage,
  value,
  label,
  className,
  onChange,
  onBlur,
  ...props
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const id = useId();
  const error = !!errorMessage?.length;

  useEffect(() => {
    const textArea = textareaRef.current;
    if (!textArea) return;

    textArea.style.height = 'auto';
    textArea.style.height = `${textArea.scrollHeight}px`;
  }, [value]);

  return (
    <div
      className={classes(['dropin-textarea-container', className])}
      data-testid="dropin-textarea-container"
    >
      <textarea
        ref={textareaRef}
        data-testid="dropin-textarea-field"
        className={classes([
          'dropin-textarea',
          [`dropin-textarea--error`, error],
          ['dropin-textarea--disabled', !!disabled],
        ])}
        id={id}
        placeholder={label}
        name={name}
        value={value}
        disabled={disabled}
        onBlur={onBlur}
        onChange={onChange}
        {...props}
      />
      <label
        htmlFor={id}
        className={classes([
          `dropin-textarea__label--floating`,
          [`dropin-textarea__label--floating--error`, error],
        ])}
      >
        {label}
      </label>
      {error ? (
        <div
          className={classes([
            `dropin-textarea__label--floating--text`,
            [`dropin-textarea__label--floating--error`, error],
          ])}
        >
          {errorMessage}
        </div>
      ) : null}
    </div>
  );
};
